import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { fetchGrades } from './stores/grade/GradeState';
import { fetchPlaybackTypes } from './stores/playbackType/PlaybackTypeState';
import { fetchProfile } from './stores/profile/ProfileState';
import { fetchSubjects } from './stores/subject/SubjectState';
import { reloadTransaction } from './stores/transaction/Transaction';
import { setLessonSet } from './stores/lesson-list/LessonSetListState';
import { setSdkFacebook } from './stores/facebookModule/FbModule';
import { generateToken } from './utility/Token';
import { generateMobileRoutes, generatePayMobileGuestRoutes, generatePayMobilePremiumRoutes, generatePayExtensionRoutes } from './utility/PremiumPlusRoutes';

import CorrectAndIncorrectOverlay from './components/correct-and-incorrect-overlay/CorrectAndIncorrectOverlay';
import HeaderBar from './components/header-bar/HeaderBar';
import NaLogin from './components/na-login/NaLogin';
import PopUp from './components/pop-up/PopUp';
import Promote from './components/promote/Promote';
import TitleConfig from './components/title-config/TitleConfig';

import EditProfilePage from './pages/edit-profile/EditProfile';
import Exam from './pages/exam/Exam';
import ExamAnswer from './pages/exam-answer/ExamAnswer';
import ExamAnswerOld from './pages/exam-answer-old/ExamAnswerOld';
import ExamList from './pages/exam-list/ExamList';
import ExamResult from './pages/exam-result/ExamResult';
import JoinSchoolPage from './pages/join-school/JoinSchool';
import LivePage from './pages/live-stream/LiveStreamPage';
import LiveStreamHistoryPage from './pages/live-stream-history/LiveStreamHistoryPage';
import LiveStreamShowList from './pages/live-stream-show-list/LiveStreamShowList';
import LiveStreamShowTopic from './pages/live-stream-show-topic/LiveStreamShowTopic';
import LiveStreamShowTopicList from './pages/live-stream-show-topic-list/LiveStreamShowTopicList';
import MainPage from './pages/main/MainPage';
import Mobile from './pages/mobile/Mobile';
import Mobile1 from './pages/mobile-1/Mobile1';
import PayExtension from './pages/pay-extension/PayExtension';
import PayExtensionPremium from './pages/pay-extension-premium/PayExtensionPremium';
import PayExtensionPremiumPlus from './pages/pay-extension-premium-plus/PayExtensionPremiumPlus';
import PayMobile from './pages/pay-mobile/PayMobile';
import PayMobileGuest from './pages/pay-mobile-guest/PayMobileGuest';
import PayMobilePremium from './pages/pay-mobile-premium/PayMobilePremium';
import PaymentPage from './pages/payment/PaymentPage';
import PaymentHistoryPage from './pages/payment-history/PaymentHistory';
import PaymentManagementPage from './pages/payment-management/PaymentManagement';
import QuarterExamPage from './pages/quater-exam/QuarterExam';
import VODPage from './pages/vod/VODPage';
import Page404 from './pages/404/Page404';

import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';

interface PremiumPlusRouteSetting {
    code: string; premium_product_id: string; base_product_id?: string; course_id?: string;
    start_date?: string; cutoff_date?: string; line_url?: string; second_product?: boolean;
}



const App = () => {

    const dispatch = useDispatch();

    const accessData = useSelector((state: NaTypes.Store) => state.authenticate);
    const fbModule = useSelector((state: NaTypes.Store) => state.facebookModule);
    const gradeState = useSelector((state: NaTypes.Store) => state.grade);
    const subjectState = useSelector((state: NaTypes.Store) => state.subject);

    const [premiumPlusSettings, setPremiumPlusSettings] = useState<PremiumPlusRouteSetting[]>([]);
    const [routesLoaded, setRoutesLoaded] = useState(false);

    const at = accessData.accessToken ? accessData.accessToken : '';
    const aid = accessData.accountId ? accessData.accountId : '';



    const { mobileRoutes, payMobileGuestRoutes, payMobilePremiumRoutes, payExtensionRoutes } = useMemo(() => {
        if (premiumPlusSettings.length === 0) {
            return { mobileRoutes: [], payMobileGuestRoutes: [], payMobilePremiumRoutes: [], payExtensionRoutes: [] };
        }
        return {
            mobileRoutes: generateMobileRoutes(premiumPlusSettings),
            payMobileGuestRoutes: generatePayMobileGuestRoutes(premiumPlusSettings),
            payMobilePremiumRoutes: generatePayMobilePremiumRoutes(premiumPlusSettings),
            payExtensionRoutes: generatePayExtensionRoutes(premiumPlusSettings)
        };
    }, [premiumPlusSettings]);

    const fetchPremiumPlusSettings = useCallback(async () => {
        const url = `${process.env.REACT_APP_API_URL_V2}/v1/payment/product/settings/premium-plus`;
        const userToken = generateToken(aid, at);
        const headers = { 'Accept': 'application/json', 'Content-Type': 'application/json', 'token': userToken };
        try {
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) throw new Error(response.statusText);
            const { data } = await response.json();
            setPremiumPlusSettings(data);
            setRoutesLoaded(true);
        } catch (error) {
            console.error("Error fetching premium plus products.");
            setPremiumPlusSettings([]);
            setRoutesLoaded(true);
        }
    }, [aid, at]);



    useEffect(() => {
        if (at !== '' && aid !== '') {
            dispatch(reloadTransaction(at, aid));
            dispatch(fetchProfile(at, aid));
        }
    }, [at, aid, dispatch]);

    useEffect(() => {
        if (gradeState.grades.length > 0 && subjectState.subjects.length > 0) {
            dispatch(setLessonSet(gradeState.grades, subjectState.subjects));
        }
    }, [gradeState.grades, subjectState.subjects, dispatch]);

    useEffect(() => {
        if (fbModule.sdk) return;

        (window as any).fbAsyncInit = () => {
            (window as any).FB.init({
                appId: process.env.REACT_APP_FACEBOOK_ID,
                autoLogAppEvents: true,
                cookie: true,
                xfbml: true,
                version: 'v22.0',
            });
            dispatch(setSdkFacebook(true));
        };

        if (!document.getElementById('facebook-jssdk')) {
            const script = document.createElement('script');
            script.id = 'facebook-jssdk';
            script.src = 'https://connect.facebook.net/en_US/sdk.js';
            document.getElementsByTagName('script')[0].parentNode!.insertBefore(script, document.getElementsByTagName('script')[0]);
        }
    }, [fbModule.sdk, dispatch]);

    useEffect(() => {
        dispatch(fetchGrades()); dispatch(fetchSubjects()); dispatch(fetchPlaybackTypes());
    }, [dispatch]);

    useEffect(() => { fetchPremiumPlusSettings(); }, []);



    if (!routesLoaded) {
        return (
            <div className="mobile-pay-loading-container">
                <div className="mobile-pay-spinner"></div>
                <p>กำลังโหลด...</p>
            </div>
        )
    }

    return (
        <>
            <CorrectAndIncorrectOverlay />
            <NaLogin />
            <PopUp />
            <BrowserRouter>
                <TitleConfig />
                <HeaderBar />
                <Promote />
                <Switch>
                    <Route exact path="/" component={MainPage} />
                    <Route exact path="/sign_in" component={MainPage} />
                    <Route path="/examstart/:grade/:quarter/:id" component={Exam} />
                    <Route path="/examanswer/:grade/:quarter/:id/:examId" component={ExamAnswerOld} />
                    <Route path="/examanswer/:grade/:quarter/:id" component={ExamAnswer} />
                    <Route path="/exam-list" component={ExamList} />
                    <Route path="/examresult/:grade/:quarter" component={ExamResult} />
                    <Route path="/exam/:grade/:quarter" component={QuarterExamPage} />
                    <Route path="/join/school/:token" component={JoinSchoolPage} />
                    <Route path="/live/:id" component={LivePage} />
                    <Route path="/livehistory/:id" component={LiveStreamHistoryPage} />
                    <Route path="/livehistory" component={LiveStreamShowList} />
                    <Route path="/mobile-1" component={Mobile1} />

                    {mobileRoutes.map(({ path, props: routeProps }) => (
                        <Route key={path} path={path} render={(props) => <Mobile {...props} {...routeProps} />} />
                    ))}

                    <Route path="/pay/:packageId?/:token?" component={PaymentPage} />
                    <Route path="/pay-extension" component={PayExtension} />
                    <Route path="/pay-extension-premium" component={PayExtensionPremium} />
                    <Route path="/pay-mobile" component={PayMobile} />

                    {payMobileGuestRoutes.map(({ path, props: routeProps }) => (
                        <Route key={path} path={path} render={(props) => <PayMobileGuest {...props} {...routeProps} />} />
                    ))}

                    {payMobilePremiumRoutes.map(({ path, props }) => (
                        <Route key={path} path={path} render={(routeProps) => <PayMobilePremium {...routeProps} {...props} />} />
                    ))}

                    {payExtensionRoutes.map(({ path, props }) => (
                        <Route key={path} path={path} render={(routeProps) => <PayExtensionPremiumPlus {...routeProps} {...props} />} />
                    ))}

                    <Route path="/payment-history" component={PaymentHistoryPage} />
                    <Route path="/payment-management" component={PaymentManagementPage} />
                    <Route path="/playbacktopic/:topic" component={LiveStreamShowTopic} />
                    <Route path="/playbacktopic" component={LiveStreamShowTopicList} />
                    <Route path="/profile/edit" component={EditProfilePage} />
                    <Route path="/video/:subject/:grade/:topic" component={VODPage} />
                    <Route component={Page404} />
                </Switch>
            </BrowserRouter>
        </>
    );
};

export default App;
